import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Brain, Heart, Shield, TrendingUp, MessageSquare, Lock, Zap, CheckCircle2, User, MessageCircle, Crown, ArrowRight, Wand2 } from 'lucide-react';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { SubscriptionModal } from '../components/Settings/SubscriptionModal';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 50,
      damping: 15
    }
  }
};
export function PremiumToolsPage() {
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  // Reliable Pro status check
  const [profile, setProfile] = useState<{
    is_pro: boolean;
    scan_credits: number;
  } | null>(null);
  const [loadingProStatus, setLoadingProStatus] = useState(true);
  const currentPlan = user?.user_metadata?.subscriptionPlan || 'free';
  const isPro = profile?.is_pro || false;
  useEffect(() => {
    async function fetchProfile() {
      if (!user?.id) return;
      try {
        const {
          data,
          error
        } = await supabase.from('profiles').select('is_pro, scan_credits').eq('id', user.id).single();
        if (error) throw error;
        setProfile(data);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setProfile(null);
      } finally {
        setLoadingProStatus(false);
      }
    }
    fetchProfile();
    // Real-time subscription for profile updates
    const channel = supabase.channel('profile-tools-updates').on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'profiles',
      filter: `id=eq.${user?.id}`
    }, payload => {
      const newProfile = payload.new as {
        is_pro: boolean;
        scan_credits: number;
      };
      setProfile(newProfile);
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);
  const tools = [{
    icon: Wand2,
    title: 'Message Rewriter',
    description: 'Transform emotionally charged messages into healthy, effective communication with AI-powered tone coaching.',
    status: 'Free · 3/day',
    theme: 'green',
    code: 'MSG_RW_01',
    route: '/tools/rewriter',
    freeAccess: true
  }, {
    icon: Brain,
    title: 'Advanced Pattern Detection',
    description: 'AI-powered analysis to identify subtle manipulation tactics and gaslighting patterns in your conversations.',
    status: 'Pro Only',
    theme: 'red',
    code: 'MOD_AI_01',
    route: '/upload'
  }, {
    icon: Heart,
    title: 'Relationship Health Score',
    description: 'Get a comprehensive score of your relationship dynamics based on communication patterns and emotional safety.',
    status: 'Pro Only',
    theme: 'red',
    code: 'REL_SCORE_V2',
    route: '/dashboard'
  }, {
    icon: MessageSquare,
    title: 'Conversation Insights',
    description: 'Deep dive into communication styles, response times, and hidden meanings behind the texts.',
    status: 'Pro Only',
    theme: 'red',
    code: 'TXT_DECODE',
    route: '/all-scans'
  }, {
    icon: User,
    title: 'ToxicPersona AI',
    description: 'Simulate conversations with specific toxic personality types to practice setting boundaries safely.',
    status: 'Gold Tier',
    subStatus: 'Coming Soon',
    theme: 'gold',
    code: 'SIM_ENGINE',
    route: null
  }, {
    icon: MessageCircle,
    title: 'Chat Analyzer',
    description: 'Real-time analysis of ongoing chats to detect passive-aggression and manipulation as it happens.',
    status: 'Gold Tier',
    subStatus: 'Coming Soon',
    theme: 'gold',
    code: 'RT_MONITOR',
    route: null
  }, {
    icon: TrendingUp,
    title: 'Emotional Tone Analysis',
    description: 'Track emotional shifts throughout conversations to understand triggers and mood patterns.',
    status: 'Coming Soon',
    theme: 'gray',
    code: 'EMO_TRACK',
    route: null
  }, {
    icon: Shield,
    title: 'Manipulation Shield',
    description: 'Real-time alerts for potential manipulation attempts and suggestions for healthy boundaries.',
    status: 'Coming Soon',
    theme: 'gray',
    code: 'DEF_SYS_01',
    route: null
  }, {
    icon: Lock,
    title: 'Trust Metrics',
    description: 'Scientific evaluation of consistency and honesty markers in your digital interactions.',
    status: 'Coming Soon',
    theme: 'gray',
    code: 'TRU_METRIC',
    route: null
  }];
  const benefits = ['Unlimited conversation analysis', 'Priority processing speed', 'Detailed PDF reports', 'Historical trend tracking', 'Expert-backed recommendations'];
  const getThemeStyles = (theme: string) => {
    switch (theme) {
      case 'red':
        return {
          iconBg: 'bg-[#FF0000]/10 text-[#FF0000] border border-[#FF0000]/30',
          badge: 'bg-[#FF0000]/10 text-[#FF0000] border border-[#FF0000]/30',
          border: 'border-gray-800 hover:border-[#FF0000]',
          hoverShadow: 'hover:shadow-[0_0_20px_rgba(255,0,0,0.1)]'
        };
      case 'green':
        return {
          iconBg: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30',
          badge: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30',
          border: 'border-gray-800 hover:border-emerald-500',
          hoverShadow: 'hover:shadow-[0_0_20px_rgba(16,185,129,0.1)]'
        };
      case 'gold':
        return {
          iconBg: 'bg-[#FFD700]/10 text-[#FFD700] border border-[#FFD700]/30',
          badge: 'bg-[#FFD700]/10 text-[#FFD700] border border-[#FFD700]/30',
          border: 'border-gray-800 hover:border-[#FFD700]',
          hoverShadow: 'hover:shadow-[0_0_20px_rgba(255,215,0,0.1)]'
        };
      default:
        // gray
        return {
          iconBg: 'bg-gray-800 text-gray-400 border border-gray-700',
          badge: 'bg-gray-800 text-gray-400 border border-gray-700',
          border: 'border-gray-800 hover:border-gray-600',
          hoverShadow: 'hover:shadow-none'
        };
    }
  };
  const handleToolClick = (route: string | null, freeAccess?: boolean) => {
    if (!route) return;
    if (isPro || freeAccess) {
      navigate(route);
    } else {
      setShowSubscriptionModal(true);
    }
  };
  return <div className="flex flex-col w-full min-h-screen bg-[#050505] relative overflow-hidden" data-id="element-9641">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none fixed" data-id="element-9642" />

      <WebsiteHeader data-id="element-9643" />

      <div className="max-w-7xl mx-auto w-full relative z-10" data-id="element-9644">
        <main className="px-6 pt-12 pb-24" data-id="element-9645">
          {/* Hero Section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16 max-w-3xl mx-auto" data-id="element-9646">
            <div className="inline-flex items-center justify-center px-4 py-1.5 bg-[#FFD700]/10 border border-[#FFD700]/30 mb-6 shadow-[0_0_15px_rgba(255,215,0,0.1)]" data-id="element-9647">
              <Sparkles className="w-4 h-4 text-[#FFD700] mr-2" data-id="element-9648" />
              <span className="text-xs font-bold text-[#FFD700] uppercase tracking-widest font-mono" data-id="element-9649">
                Premium Features
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-6 tracking-tight leading-tight uppercase" data-id="element-9650">
              Advanced Tools for <br data-id="element-9651" />
              <span className="text-[#FF0000]" data-id="element-9652">Deeper Insights</span>
            </h1>
            <p className="text-lg text-gray-400 leading-relaxed max-w-2xl mx-auto font-mono" data-id="element-9653">
              Unlock the full potential of our AI analysis to better understand
              your relationships and protect your emotional well-being with
              professional-grade tools.
            </p>
          </motion.div>

          {/* Tools Grid */}
          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20" data-id="element-9654">
            {tools.map((tool, index) => {
            const Icon = tool.icon;
            const styles = getThemeStyles(tool.theme);
            const isComingSoon = tool.status.includes('Coming Soon') || tool.subStatus?.includes('Coming Soon');
            const isClickable = !isComingSoon && !loadingProStatus;
            return <motion.div key={index} variants={itemVariants} whileHover={isClickable ? {
              y: -4
            } : {}} onClick={() => isClickable && handleToolClick(tool.route, tool.freeAccess)} className={`group relative bg-[#0a0a0a] p-8 border ${styles.border} ${isClickable ? styles.hoverShadow : ''} transition-all duration-300 overflow-hidden ${isClickable ? 'cursor-pointer' : 'opacity-80'}`} data-id="element-9655">
                  {/* Corner Brackets */}
                  <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9656" />
                  <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9657" />
                  <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9658" />
                  <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9659" />

                  {/* Scanning Line on Hover */}
                  {isClickable && <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#FF0000] to-transparent opacity-0 group-hover:opacity-50 group-hover:animate-[scan_2s_ease-in-out_infinite]" data-id="element-9660" />}

                  <div className="flex justify-between items-start mb-6" data-id="element-9661">
                    <div className={`p-3 ${styles.iconBg} transition-colors duration-300`} data-id="element-9662">
                      <Icon size={24} strokeWidth={2} data-id="element-9663" />
                    </div>
                    <div className="flex flex-col items-end" data-id="element-9664">
                      <span className={`text-[10px] font-bold px-2 py-1 uppercase tracking-widest font-mono ${styles.badge}`} data-id="element-9665">
                        {tool.status}
                      </span>
                      {tool.subStatus && <span className="text-[10px] text-gray-500 font-mono mt-1" data-id="element-9666">
                          {tool.subStatus}
                        </span>}
                    </div>
                  </div>

                  <div className="mb-2 flex items-center justify-between" data-id="element-9667">
                    <h3 className={`text-lg font-bold text-white uppercase tracking-wide ${isClickable ? 'group-hover:text-[#FF0000]' : ''} transition-colors`} data-id="element-9668">
                      {tool.title}
                    </h3>
                  </div>

                  <div className="text-[10px] text-gray-600 font-mono mb-4 uppercase tracking-widest" data-id="element-9669">
                    Code: {tool.code}
                  </div>

                  <p className="text-gray-400 text-sm leading-relaxed mb-6 font-mono" data-id="element-9670">
                    {tool.description}
                  </p>

                  {!isComingSoon && !loadingProStatus && <div className="pt-4 border-t border-gray-800 flex items-center justify-between w-full" data-id="element-9671">
                      {isPro || tool.freeAccess ? <span className={`flex items-center ${tool.freeAccess && !isPro ? 'text-emerald-400' : 'text-[#FF0000]'} text-xs font-bold uppercase tracking-widest transition-colors`} data-id="element-9672">
                          {tool.freeAccess && !isPro ? 'Try Free' : 'Access Tool'}
                          <ArrowRight size={14} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-9673" />
                        </span> : <span className="flex items-center text-gray-400 text-xs font-bold uppercase tracking-widest group-hover:text-[#FF0000] transition-colors" data-id="element-9674">
                          <Lock size={14} className="mr-2 text-gray-500 group-hover:text-[#FF0000] transition-colors" data-id="element-9675" />
                          Unlock Access
                        </span>}
                    </div>}
                </motion.div>;
          })}
          </motion.div>

          {/* Benefits & CTA Section */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.98
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="relative bg-[#0a0a0a] border border-[#FF0000]/30 p-8 md:p-12 overflow-hidden" data-id="element-9676">
            {/* Corner Brackets (Thick) */}
            <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#FF0000]" data-id="element-9677" />
            <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-[#FF0000]" data-id="element-9678" />
            <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-[#FF0000]" data-id="element-9679" />
            <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#FF0000]" data-id="element-9680" />

            {/* Scanning Line */}
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF0000] to-transparent animate-[scan_4s_ease-in-out_infinite]" data-id="element-9681" />

            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-12" data-id="element-9682">
              <div className="flex-1 max-w-2xl" data-id="element-9683">
                <div className="flex items-center gap-2 mb-4" data-id="element-9684">
                  <Crown className="w-5 h-5 text-[#FFD700]" data-id="element-9685" />
                  <span className="text-[#FFD700] font-bold text-xs tracking-widest uppercase font-mono" data-id="element-9686">
                    Pro Membership
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-black mb-6 text-white tracking-tight uppercase" data-id="element-9687">
                  Ready to get the{' '}
                  <span className="text-[#FF0000]" data-id="element-9688">full picture</span>?
                </h2>
                <p className="text-gray-400 mb-8 text-lg leading-relaxed font-mono" data-id="element-9689">
                  Upgrade to Pro today and get immediate access to all current
                  and future premium tools, ensuring you never miss a critical
                  insight.
                </p>

                <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8 mb-8" data-id="element-9690">
                  {benefits.map((benefit, index) => <li key={index} className="flex items-center text-gray-300" data-id="element-9691">
                      <CheckCircle2 className="w-5 h-5 mr-3 text-[#FF0000] flex-shrink-0" data-id="element-9692" />
                      <span className="text-sm font-mono" data-id="element-9693">{benefit}</span>
                    </li>)}
                </ul>
              </div>

              <div className="flex-shrink-0 w-full lg:w-auto" data-id="element-9694">
                {!isPro && !loadingProStatus ? <motion.button whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} onClick={() => setShowSubscriptionModal(true)} className="w-full lg:w-auto bg-[#FF0000] hover:bg-red-600 text-white font-bold py-4 px-8 shadow-[0_0_20px_rgba(255,0,0,0.4)] transition-all flex items-center justify-center group uppercase tracking-wider" data-id="element-9695">
                    <Zap className="w-5 h-5 mr-2 fill-white group-hover:scale-110 transition-transform" data-id="element-9696" />
                    Upgrade to Pro
                  </motion.button> : <div className="bg-[#050505] border border-[#FFD700]/30 p-8 text-center min-w-[280px]" data-id="element-9697">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-[#FFD700]/10 border border-[#FFD700] rounded-full mb-4 shadow-[0_0_15px_rgba(255,215,0,0.2)]" data-id="element-9698">
                      <CheckCircle2 className="w-7 h-7 text-[#FFD700]" data-id="element-9699" />
                    </div>
                    <h3 className="font-bold text-xl mb-2 text-white uppercase tracking-wide" data-id="element-9700">
                      Pro Active
                    </h3>
                    <p className="text-gray-400 text-xs font-mono uppercase tracking-widest" data-id="element-9701">
                      Full Access Granted
                    </p>
                  </div>}
              </div>
            </div>
          </motion.div>
        </main>
      </div>

      <SubscriptionModal isOpen={showSubscriptionModal} onClose={() => setShowSubscriptionModal(false)} currentPlan={currentPlan} data-id="element-9702" />
    </div>;
}